import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import {
  doInitLU,
  luHandleAppiError,
  normalizeU3121InitLUPersonalizationFromApi,
  type U3121InitLUPersonalization,
  type U3121Task,
  type U3121WorkNature,
} from "./U3121Service";
import type { U3120Country, U3120CustomerType, U3120State } from "../u3120Customer/U3120Service";
import { U3121ContextProvider } from "./U3121Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

export const NS = "u3121";

export const U3121ROLES = {
  READ: "U3121:READ",
  ADD: "U3121:ADD",
  EDIT: "U3121:EDIT",
  DELETE: "U3121:DELETE",
  AUDITS: "U3121:AUDITS",
  EXPORT: "U3121:EXPORT",
  /** Action pour l’onglet Taux horaire (projet / sous-projet) : Ajouter, Modifier, Supprimer. */
  VIEWEMPLOYEERATE: "U3121:VIEWEMPLOYEERATE",
} as const;
export type U3121ROLES = (typeof U3121ROLES)[keyof typeof U3121ROLES];

export interface U3121InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  tasks?: U3121Task[];
  /** Catalogue natures des travaux (pr_work_nature). */
  workNatures?: U3121WorkNature[];
  /** Types de client pour le dialogue « Nouveau client » (création rapide). */
  customerTypes?: U3120CustomerType[];
  /** Pays et province/état par défaut pour le dialogue « Nouveau client » (tableaux d’un élément, comme U3120). */
  defaultCountry?: U3120Country[];
  defaultState?: U3120State[];
  /**
   * BL-0495 — perso `adm_u3500_enable_ged_nas` (opt-in).
   * Défaut `false` : masquer onglet Documents + scan. Si `true` : Documents selon rôle U3500:READ.
   */
  enableGedNas?: boolean;
  /** Personnalisations d'organisation (accès via data.personalization). Valeurs nulles si non configurées. */
  personalization?: U3121InitLUPersonalization;
}

const U3121: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3121InitLU>({
    launchSearch: false,
    hideSearch: true,
    nbRecordPerPage: 50,
    tasks: [],
    workNatures: [],
    enableGedNas: false,
    personalization: normalizeU3121InitLUPersonalizationFromApi(null),
  });

  useEffect(() => {
    i18next.loadNamespaces(["u3121"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3121InitLU>({
    doInitLUFn: async () => await doInitLU(u2000AxiosAuthenticated),
    onSuccess: (initLU: U3121InitLU) => {
      const merged: U3121InitLU = {
        ...initLU,
        enableGedNas: initLU.enableGedNas === true,
        personalization: normalizeU3121InitLUPersonalizationFromApi(initLU.personalization as U3121InitLUPersonalization | Record<string, unknown> | undefined),
      };
      setInitLUData(merged);
      setInitLUReady(true);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]);

  const handleInitLURef = useRef(handleInitLU);
  handleInitLURef.current = handleInitLU;
  const refreshInitLU = useCallback(() => {
    void handleInitLURef.current.getInitLU();
  }, []);

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);
    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3121ContextProvider initLU={initLUData} refreshInitLU={refreshInitLU}>
      <Outlet />
    </U3121ContextProvider>
  );
};

export default U3121;
